import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileText } from "lucide-react";
import { toast } from "sonner";

interface PatientNoteInputProps {
  patientId: string;
  onNoteCreated: (noteData: {
    documentId: string;
    fileName: string;
    extractedText: string;
  }) => void;
  onCancel: () => void;
}

export default function PatientNoteInput({
  patientId,
  onNoteCreated,
  onCancel,
}: PatientNoteInputProps) {
  const [noteTitle, setNoteTitle] = useState("");
  const [noteContent, setNoteContent] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async () => {
    if (!noteTitle.trim()) {
      toast.error("Por favor, insira um título para a anotação");
      return;
    }

    if (!noteContent.trim()) {
      toast.error("Por favor, insira o conteúdo da anotação");
      return;
    }

    setIsProcessing(true);

    try {
      // Criar um ID único para esta anotação manual
      const noteId = `note-${Date.now()}-${Math.random().toString(36).substring(7)}`;
      const fileName = `${noteTitle}.txt`;

      // Passar dados para validação (sem salvar ainda)
      onNoteCreated({
        documentId: noteId,
        fileName: fileName,
        extractedText: noteContent,
      });

      toast.success("Anotação criada! Agora revise e valide as informações.");
    } catch (error) {
      console.error("Erro ao processar anotação:", error);
      toast.error("Erro ao processar anotação");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Nova Anotação Clínica
        </CardTitle>
        <CardDescription>
          Registre observações, histórico ou informações relevantes sobre o paciente
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="note-title">Título da Anotação</Label>
          <Input
            id="note-title"
            placeholder="Ex: Consulta de acompanhamento - 13/10/2025"
            value={noteTitle}
            onChange={(e) => setNoteTitle(e.target.value)}
            disabled={isProcessing}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="note-content">Conteúdo</Label>
          <Textarea
            id="note-content"
            placeholder="Descreva as observações clínicas, histórico do paciente, resultados de exames observados, ou qualquer informação relevante..."
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            disabled={isProcessing}
            rows={10}
            className="resize-none"
          />
          <p className="text-sm text-muted-foreground">
            Após criar a anotação, você poderá revisar e a IA gerará insights automaticamente
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isProcessing}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isProcessing || !noteTitle.trim() || !noteContent.trim()}
          >
            {isProcessing ? "Processando..." : "Criar Anotação"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
